import _ from "lodash";
import { roundValue, toDecimal } from "src/util/decimal-utils";

/**
 * Safely parses item entries that may be JSON strings or already parsed objects.
 *
 * @param {Array<string|object>} items - Array of items to parse.
 * @returns {Array<object>} Filtered array of parsed JSON objects.
 */
export const parsedItems = (items) => {
  return items
    .map((item) => {
      try {
        return typeof item === "string" ? JSON.parse(item) : item;
      } catch (e) {
        console.error("Failed to parse item:", item, e);
        return null;
      }
    })
    .filter((item) => item !== null);
};

/**
 * Computes sale amounts (material, labor, subcontractor) and total sale amount for a subtask.
 * Supports proposal mode (pre-computed numeric columns) and legacy fallback (parsed JSON arrays).
 *
 * @param {object} subTask - SubTask object.
 * @returns {object} Calculated monetary amounts using Decimal.js precision.
 */
export const calculateSubTaskSaleAmounts = (subTask) => {
  let materialSaleAmount = 0;
  let laborSaleAmount = 0;
  let subcontractorSaleAmount = 0;

  if (
    subTask.materialSaleAmount !== undefined ||
    subTask.laborSaleAmount !== undefined ||
    subTask.subcontractorSaleAmount !== undefined
  ) {
    materialSaleAmount = roundValue(parseFloat(subTask.materialSaleAmount) || 0, 2);
    laborSaleAmount = roundValue(parseFloat(subTask.laborSaleAmount) || 0, 2);
    subcontractorSaleAmount = roundValue(parseFloat(subTask.subcontractorSaleAmount) || 0, 2);
  } else {
    const parsedMaterial = parsedItems(subTask.material || []);
    const parsedLabor = parsedItems(subTask.labor || []);
    const parsedSubContractor = parsedItems(subTask.subcontractor || []);
    materialSaleAmount = parsedMaterial.reduce(
      (sum, item) => toDecimal(sum).plus(toDecimal(item.total || 0)).toNumber(),
      0
    );
    laborSaleAmount = parsedLabor.reduce(
      (sum, item) => toDecimal(sum).plus(toDecimal(item.total || 0)).toNumber(),
      0
    );
    subcontractorSaleAmount = parsedSubContractor.reduce(
      (sum, item) => toDecimal(sum).plus(toDecimal(item.saleAmount || 0)).toNumber(),
      0
    );
  }

  const totalSaleAmount = roundValue(
    toDecimal(materialSaleAmount)
      .plus(toDecimal(laborSaleAmount))
      .plus(toDecimal(subcontractorSaleAmount))
      .toNumber(),
    2
  );

  return {
    materialSaleAmount: roundValue(materialSaleAmount, 2),
    laborSaleAmount: roundValue(laborSaleAmount, 2),
    subcontractorSaleAmount: roundValue(subcontractorSaleAmount, 2),
    totalSaleAmount,
  };
};

/**
 * Associates subtasks with their respective parent tasks inside sections hierarchy
 * and aggregates sale amounts using Decimal precision.
 *
 * @param {object} sections - Sections map keyed by section ID.
 * @param {Array<object>} subTasks - List of subtasks to append.
 * @returns {object} Updated sections dictionary.
 */
export const addSubTasksToSections = (sections, subTasks) => {
  if (!subTasks || subTasks.length === 0 || _.isEmpty(sections))
    return sections;
  const subTaskMap = new Map();
  subTasks.forEach((subTask) => {
    if (!subTaskMap.has(subTask.taskId)) {
      subTaskMap.set(subTask.taskId, []);
    }

    const {
      materialSaleAmount,
      laborSaleAmount,
      subcontractorSaleAmount,
      totalSaleAmount,
    } = calculateSubTaskSaleAmounts(subTask);

    subTask.materialSaleAmount = materialSaleAmount;
    subTask.laborSaleAmount = laborSaleAmount;
    subTask.subcontractorSaleAmount = subcontractorSaleAmount;
    subTask.totalSaleAmount = totalSaleAmount;
    subTaskMap.get(subTask.taskId).push(subTask);
  });

  const updatedSections = Object.fromEntries(
    Object.entries(sections).map(([sectionId, section]) => {
      if (!section.taskList || !section.taskList.tasks) {
        return [sectionId, { ...section }];
      }

      const updatedTasks = Object.fromEntries(
        Object.entries(section.taskList.tasks).map(([taskId, task]) => {
          if (subTaskMap.has(taskId)) {
            const taskSubTasks = subTaskMap.get(taskId);
            const materialSaleAmount = roundValue(
              taskSubTasks.reduce((sum, st) => toDecimal(sum).plus(toDecimal(st.materialSaleAmount || 0)).toNumber(), 0)
            );
            const laborSaleAmount = roundValue(
              taskSubTasks.reduce((sum, st) => toDecimal(sum).plus(toDecimal(st.laborSaleAmount || 0)).toNumber(), 0)
            );
            const subcontractorSaleAmount = roundValue(
              taskSubTasks.reduce((sum, st) => toDecimal(sum).plus(toDecimal(st.subcontractorSaleAmount || 0)).toNumber(), 0)
            );
            const totalSaleAmount = roundValue(
              toDecimal(materialSaleAmount)
                .plus(toDecimal(laborSaleAmount))
                .plus(toDecimal(subcontractorSaleAmount))
                .toNumber(),
              2
            );

            const updatedTask = {
              ...task,
              subTasks: [...taskSubTasks],
              materialSaleAmount,
              laborSaleAmount,
              subcontractorSaleAmount,
              totalSaleAmount,
            };
            return [taskId, updatedTask];
          }
          return [taskId, { ...task }];
        })
      );

      return [
        sectionId,
        {
          ...section,
          taskList: {
            ...section.taskList,
            tasks: updatedTasks,
          },
        },
      ];
    })
  );

  return updatedSections;
};

/**
 * Associates subtasks with their parent options/variations and aggregates sale amounts.
 *
 * @param {object} optionList - Option dictionary keyed by option ID.
 * @param {Array<object>} subTasks - List of subtasks to append.
 * @returns {object} Updated option dictionary.
 */
export const addSubTasksToOptions = (optionList, subTasks) => {
  if (!subTasks || subTasks.length === 0 || _.isEmpty(optionList))
    return optionList;
  const subTaskMap = new Map();
  subTasks.forEach((subTask) => {
    if (!subTaskMap.has(subTask.taskId)) {
      subTaskMap.set(subTask.taskId, []);
    }

    const {
      materialSaleAmount,
      laborSaleAmount,
      subcontractorSaleAmount,
      totalSaleAmount,
    } = calculateSubTaskSaleAmounts(subTask);

    subTask.materialSaleAmount = materialSaleAmount;
    subTask.laborSaleAmount = laborSaleAmount;
    subTask.subcontractorSaleAmount = subcontractorSaleAmount;
    subTask.totalSaleAmount = totalSaleAmount;
    subTaskMap.get(subTask.taskId).push(subTask);
  });

  const updatedOptionList = Object.fromEntries(
    Object.entries(optionList).map(([optionId, option]) => {
      if (subTaskMap.has(optionId)) {
        const optionSubTasks = subTaskMap.get(optionId);
        const materialSaleAmount = roundValue(
          optionSubTasks.reduce((sum, st) => toDecimal(sum).plus(toDecimal(st.materialSaleAmount || 0)).toNumber(), 0)
        );
        const laborSaleAmount = roundValue(
          optionSubTasks.reduce((sum, st) => toDecimal(sum).plus(toDecimal(st.laborSaleAmount || 0)).toNumber(), 0)
        );
        const subcontractorSaleAmount = roundValue(
          optionSubTasks.reduce((sum, st) => toDecimal(sum).plus(toDecimal(st.subcontractorSaleAmount || 0)).toNumber(), 0)
        );
        const totalSaleAmount = roundValue(
          toDecimal(materialSaleAmount)
            .plus(toDecimal(laborSaleAmount))
            .plus(toDecimal(subcontractorSaleAmount))
            .toNumber(),
          2
        );

        const updatedOption = {
          ...option,
          subTasks: [...optionSubTasks],
          materialSaleAmount,
          laborSaleAmount,
          subcontractorSaleAmount,
          totalSaleAmount,
        };
        return [optionId, updatedOption];
      }
      return [optionId, { ...option }];
    })
  );

  return updatedOptionList;
};
